import {
  BlockConnectionPolicyReason,
  BLOCK_CONNECTION_REASON_TO_CATEGORY,
  BLOCK_POLICY_REASON_LABEL,
  ConnectionType,
  MIN_BLOCK_POLICY_NOTE_LENGTH,
} from '@traba/types'
import intersection from 'lodash/intersection'

export { MIN_BLOCK_POLICY_NOTE_LENGTH }

const CONNECTION_EXCLUSIVITY_MAP = {
  [ConnectionType.BLOCK]: [
    ConnectionType.FAVORITE,
    ConnectionType.TEMP_TO_HIRE,
  ],
  [ConnectionType.SCHEDULED_BLOCK]: [
    ConnectionType.FAVORITE,
    ConnectionType.TEMP_TO_HIRE,
  ],
  [ConnectionType.INELIGIBLE]: [
    ConnectionType.FAVORITE,
    ConnectionType.TEMP_TO_HIRE,
  ],
  [ConnectionType.FAVORITE]: [
    ConnectionType.BLOCK,
    ConnectionType.SCHEDULED_BLOCK,
    ConnectionType.INELIGIBLE,
  ],
  [ConnectionType.TEMP_TO_HIRE]: [
    ConnectionType.BLOCK,
    ConnectionType.SCHEDULED_BLOCK,
    ConnectionType.INELIGIBLE,
  ],
  [ConnectionType.VETTED]: [],
  [ConnectionType.SOURCED]: [],
}

export function isConnectionType(
  connectionType: ConnectionType | string,
): connectionType is ConnectionType {
  return Object.keys(ConnectionType).includes(connectionType)
}

export const isBlockConnection = (
  connectionType: ConnectionType | undefined,
) => {
  return connectionType === ConnectionType.BLOCK
}

/** Policy keys → ops label; otherwise pass-through (legacy reasons).
 * TODO ple: If legacy free-text ever equals a policy enum string, labels could mis-map until we persist reason provenance (policy vs legacy). */
export function formatStoredConnectionReasonLabel(reason: string): string {
  if (
    Object.prototype.hasOwnProperty.call(
      BLOCK_CONNECTION_REASON_TO_CATEGORY,
      reason,
    )
  ) {
    return BLOCK_POLICY_REASON_LABEL[reason as BlockConnectionPolicyReason]
  }
  return reason
}

export function connectionTypeUsesBlockPolicy(
  connectionType: ConnectionType | undefined,
): boolean {
  if (connectionType === undefined) {
    return false
  }
  return (
    connectionType === ConnectionType.BLOCK ||
    connectionType === ConnectionType.SCHEDULED_BLOCK
  )
}

export function isBlockPolicyNoteSufficient(note: string): boolean {
  return note.trim().length >= MIN_BLOCK_POLICY_NOTE_LENGTH
}

export function connectionTypeShowsBlockReasonEditInOps(
  connectionType: ConnectionType | undefined,
  isBlockConnectionPolicyEnabled: boolean,
): boolean {
  if (connectionType === undefined) {
    return false
  }
  if (connectionType === ConnectionType.BLOCK) {
    return true
  }
  if (!isBlockConnectionPolicyEnabled) {
    return false
  }
  return connectionType === ConnectionType.SCHEDULED_BLOCK
}

export const isIneligibleConnection = (
  connectionType: ConnectionType | undefined,
) => {
  return connectionType === ConnectionType.INELIGIBLE
}

export const isVettedConnection = (
  connectionType: ConnectionType | undefined,
) => {
  return connectionType === ConnectionType.VETTED
}

export function checkConnectionConflicts(
  toAdd: ConnectionType,
  connections?: ConnectionType[],
) {
  if (!toAdd) {
    return {
      isValid: true,
    }
  }

  if (connections?.includes(toAdd)) {
    return {
      isValid: false,
      message: `Connection of type ${toAdd} already exists`,
    }
  }

  const conflicting = intersection(
    connections,
    CONNECTION_EXCLUSIVITY_MAP[toAdd],
  )
  if (conflicting.length === 0) {
    return {
      isValid: true,
    }
  }

  const conflictingString = conflicting.join(', ')

  return {
    isValid: false,
    message: `Adding ${toAdd} connection will overwrite existing ${conflictingString} connection${conflicting.length > 1 ? 's' : ''}`,
  }
}
